// All display formatting below uses Intl.DateTimeFormat with an explicit `timeZone`, so it
// renders correct America/Chicago wall-clock times regardless of the viewer's own device
// timezone. See shared/timezone.ts for why we don't use date-fns-tz here.

import { getZonedParts, zonedWallClockToUtc } from "@shared/timezone";

export const APP_TIMEZONE = "America/Chicago";

const timeFormatters = new Map<string, Intl.DateTimeFormat>();
function timeFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = timeFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "2-digit" });
    timeFormatters.set(timeZone, f);
  }
  return f;
}

const weekdayFormatters = new Map<string, Intl.DateTimeFormat>();
function weekdayFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = weekdayFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long" });
    weekdayFormatters.set(timeZone, f);
  }
  return f;
}

const dateHeadingFormatters = new Map<string, Intl.DateTimeFormat>();
function dateHeadingFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = dateHeadingFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long", month: "long", day: "numeric" });
    dateHeadingFormatters.set(timeZone, f);
  }
  return f;
}

const dateShortFormatters = new Map<string, Intl.DateTimeFormat>();
function dateShortFormatter(timeZone: string): Intl.DateTimeFormat {
  let f = dateShortFormatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", year: "numeric" });
    dateShortFormatters.set(timeZone, f);
  }
  return f;
}

export function formatTime(iso: string, timezone: string = APP_TIMEZONE): string {
  return timeFormatter(timezone).format(new Date(iso));
}

export function formatWeekday(iso: string, timezone: string = APP_TIMEZONE): string {
  return weekdayFormatter(timezone).format(new Date(iso));
}

export function formatDateHeading(iso: string, timezone: string = APP_TIMEZONE): string {
  return dateHeadingFormatter(timezone).format(new Date(iso));
}

export function formatDateShort(iso: string, timezone: string = APP_TIMEZONE): string {
  return dateShortFormatter(timezone).format(new Date(iso));
}

export function formatDateTimeRange(
  startIso: string,
  endIso: string,
  allDay: boolean,
  timezone: string = APP_TIMEZONE,
): string {
  if (allDay) return formatDateShort(startIso, timezone);
  const sameDay = formatDateShort(startIso, timezone) === formatDateShort(endIso, timezone);
  if (sameDay) {
    return `${formatDateHeading(startIso, timezone)} · ${formatTime(startIso, timezone)} – ${formatTime(endIso, timezone)}`;
  }
  return `${formatDateShort(startIso, timezone)} ${formatTime(startIso, timezone)} – ${formatDateShort(endIso, timezone)} ${formatTime(endIso, timezone)}`;
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** For <input type="datetime-local">: "YYYY-MM-DDTHH:mm" wall-clock in the given timezone. */
export function toDatetimeLocalValue(iso: string, timezone: string = APP_TIMEZONE): string {
  const p = getZonedParts(new Date(iso), timezone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** Converts a <input type="datetime-local"> value (wall clock in `timezone`) back to a UTC ISO string. */
export function fromDatetimeLocalValue(value: string, timezone: string = APP_TIMEZONE): string {
  const [datePart, timePart] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = (timePart ?? "00:00").split(":").map(Number);
  return zonedWallClockToUtc(year, month, day, hour, minute, 0, timezone).toISOString();
}

/** For <input type="date">: "YYYY-MM-DD", treating the stored instant as a plain calendar date. */
export function toDateValue(iso: string): string {
  return iso.slice(0, 10);
}

export function fromDateValue(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

export function startOfDayIso(date: Date, timezone: string = APP_TIMEZONE): string {
  const p = getZonedParts(date, timezone);
  return zonedWallClockToUtc(p.year, p.month, p.day, 0, 0, 0, timezone).toISOString();
}

/** "YYYY-MM-DD" calendar-day key for a UTC instant, in the given timezone. Used to group
 * occurrences by day regardless of the viewer's own device timezone. */
export function chicagoDateKey(date: Date | string, timezone: string = APP_TIMEZONE): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(d);
}

export function startOfMonthIso(anchor: Date, timezone: string = APP_TIMEZONE): string {
  const p = getZonedParts(anchor, timezone);
  return zonedWallClockToUtc(p.year, p.month, 1, 0, 0, 0, timezone).toISOString();
}

/** Adds whole months to a UTC instant, keeping it anchored to the 1st of the resulting month
 * in `timezone` (used to page the agenda view forward/backward by month). */
export function addMonthsToMonthStart(iso: string, months: number, timezone: string = APP_TIMEZONE): string {
  const p = getZonedParts(new Date(iso), timezone);
  const total = p.month - 1 + months;
  const year = p.year + Math.floor(total / 12);
  const month = ((total % 12) + 12) % 12;
  return zonedWallClockToUtc(year, month + 1, 1, 0, 0, 0, timezone).toISOString();
}

export function formatMonthTitle(iso: string, timezone: string = APP_TIMEZONE): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: timezone, month: "long", year: "numeric" }).format(new Date(iso));
}

/** "Today" / "Tomorrow" / "Yesterday" for near dates, otherwise a full weekday + date —
 * the relative labels are what make a day-grouped agenda list feel immediately readable. */
export function formatAgendaDayHeading(dayKey: string, timezone: string = APP_TIMEZONE): string {
  const now = new Date();
  const todayKey = chicagoDateKey(now, timezone);
  const tomorrowKey = chicagoDateKey(new Date(now.getTime() + 24 * 60 * 60 * 1000), timezone);
  const yesterdayKey = chicagoDateKey(new Date(now.getTime() - 24 * 60 * 60 * 1000), timezone);
  if (dayKey === todayKey) return "Today";
  if (dayKey === tomorrowKey) return "Tomorrow";
  if (dayKey === yesterdayKey) return "Yesterday";

  const [y, m, d] = dayKey.split("-").map(Number);
  const instant = zonedWallClockToUtc(y, m, d, 12, 0, 0, timezone);
  const includeYear = y !== Number(todayKey.split("-")[0]);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  }).format(instant);
}
